import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux-toolkit/store";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import { Card, CardBody, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Row } from "reactstrap";
import CardHeaderSpan from "../../../Utils/CommonComponents/CardHeaderSpan";
import Chart from "react-google-charts";
import { GoogleChartWrapperChartType } from "react-google-charts";
import { getFilteredPositionIds } from "../../../db/DexieServises";

interface MtpStatusData {
  Draft: number;
  "Not Initiated": number;
  Submitted: number;
  Approved: number;
  Rejected: number;
}

interface MonthlyMtpData {
  month: string;
  data: MtpStatusData;
}

interface MtpStatusByDesignationProps {
  selectedPositionIds?: (string | number)[];
}

const MtpStatusByDesignation: React.FC<MtpStatusByDesignationProps> = ({ selectedPositionIds }) => {
  const [monthlyData, setMonthlyData] = useState<MonthlyMtpData[]>([]);
  const [monthsList, setMonthsList] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const [monthModalOpen, setMonthModalOpen] = useState(false);
  const [monthRange, setMonthRange] = useState({
    from: { month: "", year: "" },
    to: { month: "", year: "" },
  });

  const positions = useSelector((state: RootState) => state.position.positions);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const monthNumberToName: { [key: number]: string } = {
    1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
    7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
  };

  const nameToMonthNumber: { [key: string]: number } = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
  };

  const toggleMonthModal = () => setMonthModalOpen((prev: boolean) => !prev);

  const handleApplyDateRange = () => {
    if (monthRange.from.month && monthRange.from.year && monthRange.to.month && monthRange.to.year) {
      const fromMonthNum = nameToMonthNumber[monthRange.from.month];
      const toMonthNum = nameToMonthNumber[monthRange.to.month];
      setDateRange({
        start: `${fromMonthNum}-${monthRange.from.year}`,
        end: `${toMonthNum}-${monthRange.to.year}`,
      });
    }
    toggleMonthModal();
  };

  const getMonthRangeButtonLabel = () => {
    if (dateRange.start && dateRange.end) {
      const [startMonth, startYear] = dateRange.start.split("-");
      const [endMonth, endYear] = dateRange.end.split("-");
      return `${monthNumberToName[parseInt(startMonth, 10)]} ${startYear} - ${monthNumberToName[parseInt(endMonth, 10)]} ${endYear}`;
    }
    return "Select Month Range";
  };

  /**
   * Fetch MTP Status data for given positions and month
   */
  const fetchMtpStatus = useCallback(
    async (positionIds: string, month: string): Promise<MtpStatusData | null> => {
      const serverUrl = localStorage.getItem("serverUrl");
      if (!serverUrl) {
        console.error("Server URL not found in localStorage.");
        return null;
      }

      // Convert month format from "yyyy-MM" to "MM-yyyy" for API
      const [year, monthNum] = month.split("-");
      const apiMonth = `${monthNum}-${year}`;

      const url = `${serverUrl}${API.mtpStatusByDesignation}`;
      console.log("============mtpStatusByDesignation============", url, apiMonth, positionIds);

      try {
        const { status, data } = await getData(url, true, {
          moye: apiMonth,
          position_id: positionIds,
        });

        if (status === 200 && data.data) {
          return data.data as MtpStatusData;
        } else {
          console.error("Failed to fetch MTP status data");
          return null;
        }
      } catch (error) {
        console.error("Error fetching MTP status:", error);
        return null;
      }
    },
    []
  );

  /**
   * Process API data for all months
   */
  const processChartData = useCallback(async () => {
    // Use selectedPositionIds from props if provided, otherwise use Redux positions
    const positionIds =
      selectedPositionIds && selectedPositionIds.length > 0
        ? selectedPositionIds.join(",")
        : positions.length > 0
          ? positions.map((pos) => pos.id).join(",")
          : (await getFilteredPositionIds("Position", unitId)).join(",");

    const monthsToUse = monthsList.length > 0 ? monthsList : [];
    const allMonthlyData: MonthlyMtpData[] = [];

    for (const month of monthsToUse) {
      const apiData = await fetchMtpStatus(positionIds, month);
      if (apiData) {
        allMonthlyData.push({
          month,
          data: apiData,
        });
      }
    }

    setMonthlyData(allMonthlyData);
  }, [positions, unitId, monthsList, fetchMtpStatus, selectedPositionIds]);

  /**
   * Builds month list from selected date range
   */
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      const startMonth = parseInt(dateRange.start.split("-")[0], 10);
      const startYear = parseInt(dateRange.start.split("-")[1], 10);
      const endMonth = parseInt(dateRange.end.split("-")[0], 10);
      const endYear = parseInt(dateRange.end.split("-")[1], 10);

      const months: string[] = [];

      for (let year = startYear; year <= endYear; year++) {
        const monthStart = year === startYear ? startMonth : 1;
        const monthEnd = year === endYear ? endMonth : 12;

        for (let month = monthStart; month <= monthEnd; month++) {
          const formattedMonth = `${year}-${month.toString().padStart(2, "0")}`;
          months.push(formattedMonth);
        }
      }

      setMonthsList(months);
    } else {
      // Default to last 3 months if dateRange is not selected
      const months: string[] = [];
      const now = new Date();
      for (let i = 0; i < 3; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        months.push(`${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`);
      }
      setMonthsList(months.reverse()); // Reverse to have oldest first
    }
  }, [dateRange]);

  /**
   * Recalculate chart whenever dependencies change
   */
  useEffect(() => {
    if (monthsList.length > 0) {
      processChartData();
    }
  }, [positions, monthsList, processChartData, selectedPositionIds]);

  /**
   * Build chart data from monthly data
   */
  const buildChartData = (data: MonthlyMtpData[]): (string | number)[][] => {
    if (!data || data.length === 0) return [];

    const statuses = ["Draft", "Not Initiated", "Submitted", "Approved", "Rejected"];
    const header: (string | number)[] = ["Month", ...statuses];

    const rows = data.map((item) => {
      // Format month for display (e.g., "2025-01" -> "Jan 2025")
      const [year, monthNum] = item.month.split("-");
      const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleString("en", { month: "short" });
      const displayMonth = `${monthName} ${year}`;

      return [
        displayMonth,
        item.data.Draft ?? 0,
        item.data["Not Initiated"] ?? 0,
        item.data.Submitted ?? 0,
        item.data.Approved ?? 0,
        item.data.Rejected ?? 0,
      ];
    });

    return [header, ...rows];
  };

  const chartData = buildChartData(monthlyData);

  interface ChartConfig {
    width: string;
    height: string;
    chartType: GoogleChartWrapperChartType;
    data: (string | number)[][];
    options: any;
  }

  const columnChart1: ChartConfig = {
    width: "100%",
    height: "300px",
    chartType: "ColumnChart",
    data: chartData,
    options: {
      legend: { position: "top" },
      bar: { groupWidth: "70%" },
      colors: ["#307EF3", "#EBA31D", "#51bb25", "#f73164", "#a927f9"],
      vAxis: { format: "decimal", title: "Count" },
      hAxis: { title: "Month" },
      isStacked: false,
    },
  };

  return (
    <Col className={"col-sm-12 col-xl-6 box-col-6"}>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold fs-5">MTP Status By Designation</h5>
            <Button color="info" onClick={toggleMonthModal} size="sm">
              {getMonthRangeButtonLabel()}
            </Button>
          </div>
          <div className={"chart-block"}>
            <div className={"chart-overflow"}>
              {chartData.length > 1 ? (
                <Chart
                  width={columnChart1.width}
                  height={columnChart1.height}
                  chartType={columnChart1.chartType}
                  data={columnChart1.data}
                  options={columnChart1.options}
                  loader={<div>Loading Chart...</div>}
                />
              ) : (
                <div className="text-center text-muted">No data available</div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Date Range Modal */}
      <Modal isOpen={monthModalOpen} toggle={toggleMonthModal} centered className="mt-2">
        <ModalHeader toggle={toggleMonthModal}>Select Month Range</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <label>From</label>
              <select
                className="form-control"
                value={`${monthRange.from.month}-${monthRange.from.year}`}
                onChange={(e) => {
                  const [month, year] = e.target.value.split("-");
                  setMonthRange({ ...monthRange, from: { month, year } });
                }}
              >
                <option value="">Select Month</option>
                {yearOptions.flatMap((year) =>
                  Object.entries(monthNumberToName)
                    .sort(([a], [b]) => Number(b) - Number(a))
                    .filter(([monthNumber]) => {
                      if (year === currentYear) {
                        return Number(monthNumber) <= new Date().getMonth() + 1;
                      }
                      return true;
                    })
                    .map(([_, monthName]) => (
                      <option
                        key={`from-${monthName}-${year}`}
                        value={`${monthName}-${year}`}
                      >
                        {`${monthName}-${year}`}
                      </option>
                    ))
                )}
              </select>
            </Col>
            <Col md={6}>
              <label>To</label>
              <select
                className="form-control"
                value={`${monthRange.to.month}-${monthRange.to.year}`}
                onChange={(e) => {
                  const [month, year] = e.target.value.split("-");
                  setMonthRange({ ...monthRange, to: { month, year } });
                }}
              >
                <option value="">Select Month</option>
                {yearOptions.flatMap((year) =>
                  Object.entries(monthNumberToName)
                    .sort(([a], [b]) => Number(b) - Number(a))
                    .filter(([monthNumber]) => {
                      if (year === currentYear) {
                        return Number(monthNumber) <= new Date().getMonth() + 1;
                      }
                      return true;
                    })
                    .map(([_, monthName]) => (
                      <option
                        key={`to-${monthName}-${year}`}
                        value={`${monthName}-${year}`}
                      >
                        {`${monthName}-${year}`}
                      </option>
                    ))
                )}
              </select>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleMonthModal}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={() => {
              setMonthRange({ from: { month: "", year: "" }, to: { month: "", year: "" } });
              setDateRange({ start: "", end: "" });
              toggleMonthModal();
            }}
          >
            Clear
          </Button>
          <Button color="success" onClick={handleApplyDateRange}>
            Apply
          </Button>
        </ModalFooter>
      </Modal>
    </Col>
  );
};

export default MtpStatusByDesignation;
